<script setup lang="ts">
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import AlertCard from './AlertCard.vue';
import { dedupeAlerts, sortAlertsByPublication } from '@/libs/alerts-helper';
import type { Alert } from '@/@types/alert';
import type { Route } from '@/@types/gtfs-ressource';
import { useAlertsStore } from '@/stores/alerts';
import { useGtfsStore } from '@/stores/gtfs';

const alertsStore = useAlertsStore();
const gtfsStore = useGtfsStore();

const router = useRouter();

const props = defineProps<{
  routeId: string;
  alertId?: string;
}>();

const openAlertIds = ref<Set<string>>(new Set());

const route = computed<Route | undefined>(() => gtfsStore.gtfsData.routes[props.routeId]);

const routeAlerts = computed<Array<Alert>>(() =>
  sortAlertsByPublication(
    dedupeAlerts(
      alertsStore.alerts.filter(alert =>
        alert.informed_entity?.some(entity => entity.route_id === props.routeId),
      ),
    ),
  ),
);

/**
 * What is unfolded is decided in one place, as on the Infos view: a deep link from
 * a push notification, otherwise the first alert carrying a description.
 */
let handledAlertId: string | undefined;
let defaultOpened = false;

function openAlert(alertId: string) {
  openAlertIds.value = new Set(openAlertIds.value).add(alertId);
}

watch(
  [() => props.alertId, routeAlerts],
  async ([alertId, alerts]) => {
    // Handled once per alertId — the periodic refresh recreates the alerts array
    // and must not re-scroll the page under the passenger.
    if (alertId && alertId !== handledAlertId && alerts.some(alert => alert._id === alertId)) {
      handledAlertId = alertId;
      // The deep-linked alert is the one to read: the first one stays folded.
      defaultOpened = true;
      openAlert(alertId);

      await nextTick();
      document.getElementById(alertId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // An alert with no description never unfolds, so defaulting to one would
    // leave the view closed. Applied once: closing it must stick across refreshes.
    if (defaultOpened) return;
    const firstDetailed = alerts.find(alert => alert.description_text);
    if (!firstDetailed) return;

    defaultOpened = true;
    openAlert(firstDetailed._id);
  },
  { immediate: true },
);

onBeforeMount(() => {
  alertsStore.getAlerts();
});

function setAlertOpen(alertId: string, open: boolean) {
  if (open) {
    openAlert(alertId);
    return;
  }
  const openIds = new Set(openAlertIds.value);
  openIds.delete(alertId);
  openAlertIds.value = openIds;
}
</script>

<template>
  <div class="page">
    <div class="content route-alerts-page">
      <div class="route-alerts-page__header">
        <div class="route-alerts-page__navbar">
          <RoundedButton
            :aria-label="$t('common.back')"
            icon="arrow-left"
            size="large"
            type="outline"
            @click="router.back()"
          />
        </div>
        <template v-if="route">
          <RouteBadge :route="route" />
          <h1 class="route-alerts-page__name">{{ route.route_long_name }}</h1>
        </template>
      </div>

      <div class="route-alerts-page__list">
        <AlertCard
          v-for="alert in routeAlerts"
          :key="alert._id"
          :alert="alert"
          :open="openAlertIds.has(alert._id)"
          @update:open="setAlertOpen(alert._id, $event)"
        />

        <!-- Every alert of the line expired since the passenger followed the entry point -->
        <div v-if="routeAlerts.length === 0" class="route-alerts-page__empty">
          <p class="route-alerts-page__empty-title">{{ $t('alerts.noAlertsRoute') }}</p>
          <img class="route-alerts-page__empty-img" src="@/assets/img/zen.svg" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.route-alerts-page {
  background-color: $color-white;
  color: $color-neutral-highest;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: $spacing-2;
    padding-bottom: $spacing-5;
  }

  &__navbar {
    padding: $spacing-3 $spacing-4 $spacing-1;
  }

  &__name {
    font: 600 24px / 32px $font-poppins;
    letter-spacing: $letter-spacing-tight;
    margin: 0;
    padding: 0 $spacing-4;
  }

  .route-badge {
    align-self: flex-start;
    margin: $spacing-2 $spacing-4 0;
  }

  &__list {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
  }

  &__empty {
    padding: $spacing-6 $spacing-5;
    text-align: center;
  }

  &__empty-title {
    color: $color-neutral-highest;
    font: $text-bold-large;
    letter-spacing: $text-bold-large-spacing;
    margin: 0;
  }

  &__empty-img {
    height: auto;
    margin-top: $spacing-6;
    width: 80%;
  }
}
</style>
